import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { BankingOverviewAccount, ReportData } from '../tokens/types';
import { PdfSection } from '../primitives/PdfPrimitives';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { coverFonts, overviewColors, ptToPx } from '../tokens/designTokens';
import { fmtDate } from './formatUtils';

// ════════════════════════════════════════════════════════════════
//                      D E S I G N   T O K E N S
//
// Banking Overview as a 6-column table — Figma 157:5646.
// Replaces the previous 2-up card grid; the Figma now puts every account on
// its own row with a totals footer pinned at the bottom.
// ════════════════════════════════════════════════════════════════

const bankingTable = {
    radius: 8,
    border: 1,
    borderColor: overviewColors.border,
    rowDivider: 0.5,
    rowDividerColor: overviewColors.border,

    headerBg: '#F7F8F9',
    headerHeight: 30, // 44px in Figma → 33pt; trimmed to keep page density honest
    headerSize: 7.5, // 9px Figma → 7.5pt
    headerColor: '#6B7080',
    headerFontWeight: 600,

    rowHeight: 26, // 40px in Figma → 30pt; trimmed for PDF readability
    rowBg: '#FFFFFF',
    cellPadX: 8, // 12px in Figma → 9pt; trimmed slightly to fit A4 width
    cellPadY: 6,
    bankNameSize: 8.5,
    bankNameColor: overviewColors.ink,
    bankNameFontWeight: 600,
    cellSize: 8.5,
    cellColor: '#4A5463',
    cellMutedColor: overviewColors.inkMuted,
    negColor: '#DB2626',

    // Footer row — bigger top border, neutral grey body. Figma uses an orange
    // (#EB9424) 2pt rule but our page system already has accents on every
    // section — using orange here would clash with the section eyebrow on the
    // facing page. Falling back to ink-soft 1pt for now; revisit if the Figma
    // intent of the orange rule is "this is the totals row" rather than
    // "use orange specifically".
    footerBg: '#F7F8F9',
    footerBorderTop: 1.5,
    footerBorderColor: overviewColors.inkSoft,
    footerSize: 8.5,
    footerColor: overviewColors.ink,
    footerFontWeight: 600,

    // Column flex weights (proportional to Figma frame widths 120/130/105/105/115/97.75).
    // Sum is preserved so each cell gets the same relative slice on any page width.
    flex: {
        bankName: 18,
        accountNumber: 19,
        closingBalance: 16,
        closingDate: 16,
        odLimit: 17,
        ltmTxn: 14,
    },
} as const;

const PF = coverFonts.pdf;
const SF = coverFonts.screen;

const DASH = '—';

// Indian numbering system. Negative balances render in parentheses with red tint.
const fmtINRSigned = (n?: number): { text: string; negative: boolean } => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return { text: DASH, negative: false };
    const abs = Math.abs(n);
    const formatted = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(abs);
    return { text: n < 0 ? `(${formatted})` : formatted, negative: n < 0 };
};

const fmtINR = (n?: number): string => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return DASH;
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
};

const sumNumbers = (xs: Array<number | undefined>): number =>
    xs.reduce<number>((acc, n) => acc + (typeof n === 'number' && Number.isFinite(n) ? n : 0), 0);

type Totals = {
    closingBalance?: number;
    odSanctionLimit?: number;
    transactionsLTM?: number;
};

const computeTotals = (accounts: BankingOverviewAccount[]): Totals => {
    const hasAny = (key: keyof BankingOverviewAccount) =>
        accounts.some((a) => typeof a[key] === 'number' && Number.isFinite(a[key] as number));
    return {
        closingBalance: hasAny('closingBalance') ? sumNumbers(accounts.map((a) => a.closingBalance)) : undefined,
        odSanctionLimit: hasAny('odSanctionLimit') ? sumNumbers(accounts.map((a) => a.odSanctionLimit)) : undefined,
        transactionsLTM: hasAny('transactionsLTM') ? sumNumbers(accounts.map((a) => a.transactionsLTM)) : undefined,
    };
};

// ════════════════════════════════════════════════════════════════
//                              P D F
// ════════════════════════════════════════════════════════════════

const PdfHeaderCell: React.FC<{ flex: number; align?: 'left' | 'right'; children: React.ReactNode }> = ({
    flex,
    align = 'left',
    children,
}) => (
    <View
        style={{
            flex,
            paddingHorizontal: bankingTable.cellPadX,
            paddingVertical: bankingTable.cellPadY,
            justifyContent: 'center',
        }}
    >
        <Text
            style={{
                fontSize: bankingTable.headerSize,
                fontFamily: PF.sans,
                fontWeight: bankingTable.headerFontWeight,
                color: bankingTable.headerColor,
                textAlign: align,
            }}
        >
            {children}
        </Text>
    </View>
);

const PdfCell: React.FC<{
    flex: number;
    align?: 'left' | 'right';
    mono?: boolean;
    bold?: boolean;
    color?: string;
    children: React.ReactNode;
}> = ({ flex, align = 'left', mono, bold, color, children }) => (
    <View
        style={{
            flex,
            paddingHorizontal: bankingTable.cellPadX,
            paddingVertical: bankingTable.cellPadY,
            justifyContent: 'center',
        }}
    >
        <Text
            style={{
                fontSize: bankingTable.cellSize,
                fontFamily: mono ? PF.mono : PF.sans,
                fontWeight: bold ? 600 : 400,
                color: color ?? bankingTable.cellColor,
                textAlign: align,
            }}
        >
            {children}
        </Text>
    </View>
);

const PdfBankingOverview: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const overview = data.bankingOverview;
    if (!overview || overview.accounts.length === 0) return null;
    const totals = computeTotals(overview.accounts);
    const totalsClosing = fmtINRSigned(totals.closingBalance);
    const totalsOd = fmtINRSigned(totals.odSanctionLimit);
    return (
        <PdfSection kicker="Banking" title="Banking Overview" index={index} breakBefore={breakBefore}>
            <View
                style={{
                    borderWidth: bankingTable.border,
                    borderColor: bankingTable.borderColor,
                    borderRadius: bankingTable.radius,
                    overflow: 'hidden',
                }}
            >
                {/* Header row */}
                <View
                    style={{
                        flexDirection: 'row',
                        backgroundColor: bankingTable.headerBg,
                        minHeight: bankingTable.headerHeight,
                        borderBottomWidth: bankingTable.rowDivider,
                        borderBottomColor: bankingTable.rowDividerColor,
                    }}
                    wrap={false}
                >
                    <PdfHeaderCell flex={bankingTable.flex.bankName}>Bank Name</PdfHeaderCell>
                    <PdfHeaderCell flex={bankingTable.flex.accountNumber}>Account No.</PdfHeaderCell>
                    <PdfHeaderCell flex={bankingTable.flex.closingBalance} align="right">
                        Closing{'\n'}Balance
                    </PdfHeaderCell>
                    <PdfHeaderCell flex={bankingTable.flex.closingDate}>Closing Balance{'\n'}Date</PdfHeaderCell>
                    <PdfHeaderCell flex={bankingTable.flex.odLimit} align="right">
                        OD/CC Sanction{'\n'}Limit
                    </PdfHeaderCell>
                    <PdfHeaderCell flex={bankingTable.flex.ltmTxn} align="right">
                        Transactions{'\n'}(LTM)
                    </PdfHeaderCell>
                </View>

                {/* Body rows */}
                {overview.accounts.map((a, i) => {
                    const closing = fmtINRSigned(a.closingBalance);
                    const od = fmtINRSigned(a.odSanctionLimit);
                    return (
                        <View
                            key={a.accountId ?? i}
                            wrap={false}
                            style={{
                                flexDirection: 'row',
                                backgroundColor: bankingTable.rowBg,
                                minHeight: bankingTable.rowHeight,
                                borderBottomWidth: bankingTable.rowDivider,
                                borderBottomColor: bankingTable.rowDividerColor,
                            }}
                        >
                            <PdfCell flex={bankingTable.flex.bankName} bold color={bankingTable.bankNameColor}>
                                {a.name ?? DASH}
                            </PdfCell>
                            <PdfCell flex={bankingTable.flex.accountNumber} mono>
                                {a.accountNumber ?? DASH}
                            </PdfCell>
                            <PdfCell
                                flex={bankingTable.flex.closingBalance}
                                align="right"
                                mono
                                color={closing.negative ? bankingTable.negColor : undefined}
                            >
                                {closing.text}
                            </PdfCell>
                            <PdfCell flex={bankingTable.flex.closingDate}>
                                {a.closingBalanceDate ? fmtDate(a.closingBalanceDate) : DASH}
                            </PdfCell>
                            <PdfCell
                                flex={bankingTable.flex.odLimit}
                                align="right"
                                mono
                                color={od.negative ? bankingTable.negColor : undefined}
                            >
                                {od.text}
                            </PdfCell>
                            <PdfCell flex={bankingTable.flex.ltmTxn} align="right" mono>
                                {fmtINR(a.transactionsLTM)}
                            </PdfCell>
                        </View>
                    );
                })}

                {/* Footer (totals) */}
                <View
                    wrap={false}
                    style={{
                        flexDirection: 'row',
                        backgroundColor: bankingTable.footerBg,
                        minHeight: bankingTable.headerHeight,
                        borderTopWidth: bankingTable.footerBorderTop,
                        borderTopColor: bankingTable.footerBorderColor,
                    }}
                >
                    <PdfCell flex={bankingTable.flex.bankName} bold color={bankingTable.footerColor}>
                        All Accounts
                    </PdfCell>
                    <PdfCell flex={bankingTable.flex.accountNumber} bold color={bankingTable.footerColor}>
                        {`${overview.accounts.length} Account${overview.accounts.length === 1 ? '' : 's'}`}
                    </PdfCell>
                    <PdfCell
                        flex={bankingTable.flex.closingBalance}
                        align="right"
                        mono
                        bold
                        color={totalsClosing.negative ? bankingTable.negColor : bankingTable.footerColor}
                    >
                        {totalsClosing.text}
                    </PdfCell>
                    <PdfCell flex={bankingTable.flex.closingDate}>{''}</PdfCell>
                    <PdfCell
                        flex={bankingTable.flex.odLimit}
                        align="right"
                        mono
                        bold
                        color={totalsOd.negative ? bankingTable.negColor : bankingTable.footerColor}
                    >
                        {totalsOd.text}
                    </PdfCell>
                    <PdfCell flex={bankingTable.flex.ltmTxn} align="right" mono bold color={bankingTable.footerColor}>
                        {fmtINR(totals.transactionsLTM)}
                    </PdfCell>
                </View>
            </View>
        </PdfSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                            S C R E E N
// ════════════════════════════════════════════════════════════════

const ScreenHeaderCell: React.FC<{ flex: number; align?: 'left' | 'right'; children: React.ReactNode }> = ({
    flex,
    align = 'left',
    children,
}) => (
    <div
        style={{
            flex,
            minWidth: 0,
            paddingLeft: ptToPx(bankingTable.cellPadX),
            paddingRight: ptToPx(bankingTable.cellPadX),
            paddingTop: ptToPx(bankingTable.cellPadY),
            paddingBottom: ptToPx(bankingTable.cellPadY),
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
    >
        <span
            style={{
                fontSize: ptToPx(bankingTable.headerSize),
                fontFamily: SF.sans,
                fontWeight: bankingTable.headerFontWeight,
                color: bankingTable.headerColor,
                textAlign: align,
                whiteSpace: 'pre-line',
            }}
        >
            {children}
        </span>
    </div>
);

const ScreenCell: React.FC<{
    flex: number;
    align?: 'left' | 'right';
    mono?: boolean;
    bold?: boolean;
    color?: string;
    children: React.ReactNode;
}> = ({ flex, align = 'left', mono, bold, color, children }) => (
    <div
        style={{
            flex,
            minWidth: 0,
            paddingLeft: ptToPx(bankingTable.cellPadX),
            paddingRight: ptToPx(bankingTable.cellPadX),
            paddingTop: ptToPx(bankingTable.cellPadY),
            paddingBottom: ptToPx(bankingTable.cellPadY),
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
    >
        <span
            style={{
                fontSize: ptToPx(bankingTable.cellSize),
                fontFamily: mono ? SF.mono : SF.sans,
                fontWeight: bold ? 600 : 400,
                color: color ?? bankingTable.cellColor,
                textAlign: align,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    </div>
);

const ScreenBankingOverview: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const overview = data.bankingOverview;
    if (!overview || overview.accounts.length === 0) return null;
    const totals = computeTotals(overview.accounts);
    const totalsClosing = fmtINRSigned(totals.closingBalance);
    const totalsOd = fmtINRSigned(totals.odSanctionLimit);
    return (
        <ScreenSection kicker="Banking" title="Banking Overview" index={index}>
            <div
                style={{
                    border: `${ptToPx(bankingTable.border)}px solid ${bankingTable.borderColor}`,
                    borderRadius: ptToPx(bankingTable.radius),
                    overflow: 'hidden',
                    background: '#FFFFFF',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        background: bankingTable.headerBg,
                        minHeight: ptToPx(bankingTable.headerHeight),
                        borderBottom: `${ptToPx(bankingTable.rowDivider)}px solid ${bankingTable.rowDividerColor}`,
                    }}
                >
                    <ScreenHeaderCell flex={bankingTable.flex.bankName}>Bank Name</ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.accountNumber}>Account No.</ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.closingBalance} align="right">
                        {'Closing\nBalance'}
                    </ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.closingDate}>{'Closing Balance\nDate'}</ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.odLimit} align="right">
                        {'OD/CC Sanction\nLimit'}
                    </ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.ltmTxn} align="right">
                        {'Transactions\n(LTM)'}
                    </ScreenHeaderCell>
                </div>

                {/* Body */}
                {overview.accounts.map((a, i) => {
                    const closing = fmtINRSigned(a.closingBalance);
                    const od = fmtINRSigned(a.odSanctionLimit);
                    return (
                        <div
                            key={a.accountId ?? i}
                            style={{
                                display: 'flex',
                                background: bankingTable.rowBg,
                                minHeight: ptToPx(bankingTable.rowHeight),
                                borderBottom: `${ptToPx(bankingTable.rowDivider)}px solid ${bankingTable.rowDividerColor}`,
                            }}
                        >
                            <ScreenCell flex={bankingTable.flex.bankName} bold color={bankingTable.bankNameColor}>
                                {a.name ?? DASH}
                            </ScreenCell>
                            <ScreenCell flex={bankingTable.flex.accountNumber} mono>
                                {a.accountNumber ?? DASH}
                            </ScreenCell>
                            <ScreenCell
                                flex={bankingTable.flex.closingBalance}
                                align="right"
                                mono
                                color={closing.negative ? bankingTable.negColor : undefined}
                            >
                                {closing.text}
                            </ScreenCell>
                            <ScreenCell flex={bankingTable.flex.closingDate}>
                                {a.closingBalanceDate ? fmtDate(a.closingBalanceDate) : DASH}
                            </ScreenCell>
                            <ScreenCell
                                flex={bankingTable.flex.odLimit}
                                align="right"
                                mono
                                color={od.negative ? bankingTable.negColor : undefined}
                            >
                                {od.text}
                            </ScreenCell>
                            <ScreenCell flex={bankingTable.flex.ltmTxn} align="right" mono>
                                {fmtINR(a.transactionsLTM)}
                            </ScreenCell>
                        </div>
                    );
                })}

                {/* Footer */}
                <div
                    style={{
                        display: 'flex',
                        background: bankingTable.footerBg,
                        minHeight: ptToPx(bankingTable.headerHeight),
                        borderTop: `${ptToPx(bankingTable.footerBorderTop)}px solid ${bankingTable.footerBorderColor}`,
                    }}
                >
                    <ScreenCell flex={bankingTable.flex.bankName} bold color={bankingTable.footerColor}>
                        All Accounts
                    </ScreenCell>
                    <ScreenCell flex={bankingTable.flex.accountNumber} bold color={bankingTable.footerColor}>
                        {`${overview.accounts.length} Account${overview.accounts.length === 1 ? '' : 's'}`}
                    </ScreenCell>
                    <ScreenCell
                        flex={bankingTable.flex.closingBalance}
                        align="right"
                        mono
                        bold
                        color={totalsClosing.negative ? bankingTable.negColor : bankingTable.footerColor}
                    >
                        {totalsClosing.text}
                    </ScreenCell>
                    <ScreenCell flex={bankingTable.flex.closingDate}>{''}</ScreenCell>
                    <ScreenCell
                        flex={bankingTable.flex.odLimit}
                        align="right"
                        mono
                        bold
                        color={totalsOd.negative ? bankingTable.negColor : bankingTable.footerColor}
                    >
                        {totalsOd.text}
                    </ScreenCell>
                    <ScreenCell flex={bankingTable.flex.ltmTxn} align="right" mono bold color={bankingTable.footerColor}>
                        {fmtINR(totals.transactionsLTM)}
                    </ScreenCell>
                </div>
            </div>
        </ScreenSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                          M O D U L E   D E F
// ════════════════════════════════════════════════════════════════

export const bankingOverviewModule: ModuleDefinition = {
    meta: {
        id: 'bankingOverview',
        title: 'Banking Overview',
        description: 'Per-account closing balances, OD limits, LTM transactions and totals.',
        dataSource: 'polus/data/bankStatementData',
    },
    hasData: (d) => !!d.bankingOverview && d.bankingOverview.accounts.length > 0,
    Pdf: PdfBankingOverview,
    Screen: ScreenBankingOverview,
};
